import { useMemo } from 'react';
import useDigitalDossierActionButtons from '../../../hooks/useDigitalDossierActionButtons';
import { BRUTE_INCOME_TYPES, VALIDATION_STATUSES } from '@bcba/types';
import { useDigitalDossierSectionContext } from '../../../context/DigitalDossierSectionContext';
import { mapTaxInfoSerializerToDTO } from '../utils';
import { useToastsHook, useTranslation } from '@bcba/react-hooks';
import {
  QueryObserverResult,
  RefetchOptions,
  useMutation,
} from '@tanstack/react-query';
import { PROFIT_TYPES, ivaCondition } from '@bcba/types';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { UpdateDigitalDossierTaxInformationDto } from '../../../../../services/types/query-params';
import { SubmitHandler, useFieldArray, useForm } from '@agrotoken-tech/ui';
import { updateTaxInformation } from '../../../../../services/digital-dossiers/update-information';
import { ExplorerDigitalDossierTaxInformationSerializer } from '@bcba/serializers';
import { usePendingChangesAlert } from '../../../../../contexts/hooks';

export interface TaxInfoProps {
  data: ExplorerDigitalDossierTaxInformationSerializer;
  refetch: (
    options?: RefetchOptions | undefined,
  ) => Promise<
    QueryObserverResult<ExplorerDigitalDossierTaxInformationSerializer, Error>
  >;
}
const resolver = classValidatorResolver(UpdateDigitalDossierTaxInformationDto);

const useTaxInfoForm = ({ data, refetch }: TaxInfoProps) => {
  const ADDITIONAL_DOCUMENTS = 'additionalDocuments';
  const { t } = useTranslation();
  const { refetchDigitalDossierStatus } = useDigitalDossierSectionContext();
  const { showSuccessToast } = useToastsHook();

  const apiDataMappedToLocalState = useMemo(() => {
    return mapTaxInfoSerializerToDTO(data);
  }, [JSON.stringify(data)]);

  const formMethods = useForm({
    resolver,
    defaultValues: apiDataMappedToLocalState,
  });
  const {
    handleSubmit,
    formState: { isDirty },
    watch,
    reset,
  } = formMethods;
  const {
    fields: additionalDocumentsFields,
    append: appendAdditionalDocument,
    remove: removeAdditionalDocument,
  } = useFieldArray({
    control: formMethods.control,
    name: 'additionalDocuments',
  });
  usePendingChangesAlert(isDirty);

  // Values to read only state
  const ivaConditionReadOnlyValue = watch('ivaCondition');
  const bruteIncomeReadOnlyValue = watch('bruteIncome');
  const profitsReadOnlyValue = watch('profits');
  const jurisdictionsValues = watch('jurisdictions');
  const jurisdictionsReadOnlyValuesFormatted = jurisdictionsValues
    .map((province) =>
      province
        .toLocaleLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
    )
    .join(', ');

  const isPendingChangeRequest =
    data?.status === VALIDATION_STATUSES.PENDING_CHANGE_REQUEST;
  const validationMessage = data?.validationMessage;

  const { mutate: saveChanges, isPending: saveIsPending } = useMutation({
    mutationFn: (formData: UpdateDigitalDossierTaxInformationDto) =>
      updateTaxInformation(formData),
    onSuccess: () => {
      refetchDigitalDossierStatus();
      showSuccessToast({
        title: t('explorer.digitalDossier.taxInfo.update.success.toast'),
      });
      refetch().then((d) => {
        if (d?.data) {
          reset(mapTaxInfoSerializerToDTO(d.data), { keepDirty: false });
        }
      });
    },
  });

  const handleSave: SubmitHandler<any> = (data: any) => {
    // Filter out expirationDate and identifier from docs.
    const keys = [
      'cuitDocument',
      'iibbDocument',
      'profitsDocument',
      'cm05Document',
      'exemptionsDocument',
    ];
    keys.forEach((key) => {
      if (data[key]) {
        data[key].expirationDate = undefined;
        data[key].identifier = undefined;
        data[key].documentUuid = undefined;
      }
    });
    const additionalDocs = data['additionalDocuments'];
    if (additionalDocs) {
      additionalDocs.forEach((doc: any) => {
        doc.expirationDate = undefined;
        doc.identifier = undefined;
        doc.documentUuid = undefined;
      });
    }
    saveChanges(data);
  };

  const profitTypesOptions = Object.values(PROFIT_TYPES).map((a) => ({
    label: t(`backoffice.pages.taxInformation.PROFIT_TYPES.${a}`),
    value: a,
  }));
  const ivaConditionOptions = ivaCondition.map((a) => ({
    label: t(`backoffice.pages.taxInformation.IVA_CONDITION.${a}`),
    value: a,
  }));

  const bruteIncomeOptions = Object.values(BRUTE_INCOME_TYPES).map((a) => ({
    label: t(`backoffice.pages.taxInformation.BRUTE_INCOME_TYPES.${a}`),
    value: a,
  }));

  const { actionButtons, isFormReadOnly } = useDigitalDossierActionButtons({
    status: data.status,
    isLoadingSave: saveIsPending,
    formHasChanges: isDirty,
    handleSave: handleSubmit(handleSave),
  });

  return {
    ADDITIONAL_DOCUMENTS,
    formMethods,
    isPendingChangeRequest,
    validationMessage,
    isFormReadOnly,
    ivaConditionReadOnlyValue,
    ivaConditionOptions,
    bruteIncomeOptions,
    jurisdictionsReadOnlyValuesFormatted,
    actionButtons,
    appendAdditionalDocument,
    removeAdditionalDocument,
    additionalDocumentsFields,
    profitTypesOptions,
    profitsReadOnlyValue,
    bruteIncomeReadOnlyValue,
  };
};

export default useTaxInfoForm;
